import React from 'react';
import { toast } from 'react-toastify';
import { handleError } from '../../common/utils/util.errors';
import { deepCopy } from '../../common/utils/common.util';
import keys from 'common/react-query/data-keys';
import { useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { ProjectSummary, ProjectData, ProjectLineItemsAndSummary } from './project.type';
import ProjectRenderer from './ProjectRenderer';
import { globalUpdateProjectData, getProjectSummary } from './project.api';
import dataKeys from 'common/react-query/data-keys';
import { createAuditMessages, mapDrawsToDTO, mapLineItemsToCreateDrawLineItemDTO, validateProjectFields } from './project.util';
import { usePrompt } from '../AppPrompt';

import useLookups from '../../hooks/useLookups';
import { LOOKUP_KEYS } from '../../common/constants/lookups';
import { City, State } from '../lookups/lookups.type';

interface ProjectWrapperProps {
  projectId: string;
  projectSummary: ProjectSummary;
  isLoadingProjectSummary: boolean;
  isErrorProjectSummary: boolean;
  errorProjectSummary: any;
}

export const ProjectWrapper: React.FC<ProjectWrapperProps> = ({
  projectId,
  projectSummary,
  isLoadingProjectSummary,
  isErrorProjectSummary,
  errorProjectSummary,
}) => {
  const [project, setProject] = React.useState<ProjectData>({
    projectSummary: deepCopy(projectSummary),
    projectDraws: [],
  });
  const [initialProject, setInitialProject] = React.useState<ProjectData>({
    projectSummary: deepCopy(projectSummary),
    projectDraws: [],
  });
  const [isProjectDirty, setIsProjectDirty] = React.useState(false);
  const [isProjectLoading, setIsProjectLoading] = React.useState(false);
  const [isSaving, setIsSaving] = React.useState(false);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [isEditMode, setIsEditMode] = React.useState(false);
  const { getConfirmation } = usePrompt();
  const stateOptions = useLookups(LOOKUP_KEYS.States) as State[];
  const cityOptions = useLookups(LOOKUP_KEYS.Cities) as City[];

  const clearState = () => {
    navigate('.', { replace: true, state: null }); // clears current state
  };

  React.useEffect(() => {
    setProject(prev => ({
      ...prev,
      projectSummary: deepCopy(projectSummary),
    }));
    setInitialProject(prev => ({
      ...prev,
      projectSummary: deepCopy(projectSummary),
    }));
    setIsProjectDirty(false);
  }, [projectSummary]);

  const updateProject = (updates: Partial<ProjectData>, isEditMode: boolean, updateInitialProject?: boolean) => {
    setProject(prev => {
      const nextProject = {
        projectSummary: updates.projectSummary ?? prev.projectSummary,
        projectDraws: updates.projectDraws ?? prev.projectDraws,
        notes: updates.notes ?? prev.notes,
      };
      if (isEditMode) {
        const hasDrawChanges = nextProject.projectDraws.some(
          draw =>
            draw.isCreated ||
            draw.isEdited ||
            draw.isDeleted ||
            draw.lineItems?.some(item => item.isCreated || item.isEdited || item.isDeleted),
        );
        const hasSummaryChanges = !!nextProject.projectSummary?.isEdited;
        const hasNonDrawUpdate = !updates.projectDraws && (!!updates.projectSummary || !!updates.notes);
        setIsProjectDirty(hasDrawChanges || hasSummaryChanges || hasNonDrawUpdate);
      }
      return nextProject;
    });
    if (updateInitialProject) {
      setInitialProject(prev => ({
        projectSummary: updates.projectSummary ?? prev.projectSummary,
        projectDraws: updates.projectDraws ?? prev.projectDraws,
        notes: updates.notes ?? prev.notes,
      }));
    }
  };

  const updateInitialProject = (updatedDraw: ProjectLineItemsAndSummary) => {
    setInitialProject(prev => ({
      ...prev,
      projectDraws: prev.projectDraws.map(draw => (draw.projectDrawSummary.id === updatedDraw.projectDrawSummary.id ? updatedDraw : draw)),
    }));
  };

  const resetProject = () => {
    setProject({
      projectSummary: deepCopy(projectSummary),
      projectDraws: [],
    });
    setIsProjectDirty(false);
    clearState();
  };

  /** `payappDrawId` is set when this save commits a confirmed payapp, so the report can record it. */
  const saveProject = async (payappDrawId?: string) => {
    try {
      setIsSaving(true);

      // Only consider PayApp drafts for draws that are actually being changed in this save operation
      const drawsWithPayAppDrafts = project.projectDraws.filter(
        draw => draw.payappId && (draw.isCreated || draw.isEdited || draw.isDeleted),
      );

      if (projectId) {
        // Validate project fields
        if (!(await validateProjectFields(project?.projectSummary, cityOptions))) {
          setIsSaving(false);
          return;
        }

        // Safely handle projectDraws
        const projectDraws = project?.projectDraws || [];

        // Filter project draws and line items based on their state
        const createdDraws = projectDraws.filter(draw => draw.isCreated);
        const updatedDraws = projectDraws.filter(draw => draw.isEdited && !draw.isCreated);
        const deletedDraws = projectDraws.filter(draw => draw.isDeleted);
        const allDraws = [...createdDraws, ...updatedDraws, ...deletedDraws];

        if (drawsWithPayAppDrafts.length > 0) {
          const drawNames = drawsWithPayAppDrafts.map(draw => draw.projectDrawSummary.name).join(', ');

          const drawListItems = drawNames
            .split(', ')
            .map(name => `<li>${name}</li>`)
            .join('');
          const isConfirmed = await getConfirmation({
            heading: 'PayApp Drafts Will Be Discarded',
            description: `PayApp drafts exist for the following draws and will be permanently  discarded on save:<ul>${drawListItems}</ul>Are you sure you want to continue?`,
            confirmButton: {
              label: 'Continue',
              color: 'error',
            },
            cancelButton: {
              label: 'Cancel',
              color: 'secondary',
            },
          });

          if (!isConfirmed) {
            setIsSaving(false);
            return;
          }
        }

        // Extract DTOs for created, updated, and deleted draws
        const createDrawDTOs = mapDrawsToDTO(createdDraws, projectId);
        const updateDrawDTOs = mapDrawsToDTO(updatedDraws, projectId);
        const deleteDrawIds = deletedDraws.map(draw => draw.projectDrawSummary.id);

        // Extract line items for all draws.
        // Exclude draws with temp IDs — the backend doesn't have a real UUID yet for them,
        // so line items must be saved after the draw is committed.
        const lineItemsToCreate = projectDraws
          .filter(draw => !draw.projectDrawSummary.id?.startsWith('temp-'))
          .map(draw => ({
            drawId: draw.projectDrawSummary.id,
            lineItems: mapLineItemsToCreateDrawLineItemDTO(
              draw.projectDrawSummary.id,
              draw.lineItems?.filter(item => item.isCreated) || [],
            ),
          }))
          .filter(draw => draw.lineItems.length > 0);

        const lineItemsToUpdate = projectDraws
          .map(draw => ({
            drawId: draw.projectDrawSummary.id,
            lineItems: mapLineItemsToCreateDrawLineItemDTO(
              draw.projectDrawSummary.id,
              draw.lineItems?.filter(item => item.isEdited && !item.isCreated) || [],
            ),
          }))
          .filter(draw => draw.lineItems.length > 0);

        const lineItemsToDelete = projectDraws
          .map(draw => ({
            drawId: draw.projectDrawSummary.id,
            lineItemIds: draw.lineItems?.filter(item => item.isDeleted).map(item => item.id) || [],
            subtotalReanchors: draw.pendingSubtotalReanchors || [],
          }))
          .filter(draw => draw.lineItemIds.length > 0 || draw.subtotalReanchors.length > 0);

        // Subtotals that only ever existed client-side (e.g. carried over from a payapp merge —
        // see PayappProcessFooter.tsx) still have a temp id and haven't been persisted yet.
        // Already-persisted subtotals (created via the immediate Insert Subtotal action in
        // DrawLineItemsTable.tsx) have a real id and don't need to go through save at all.
        // If a subtotal's anchor is itself a temp id (a line item being created in this same
        // save), send it as anchorTempLineItemId so the backend can resolve it after creating
        // that line item; otherwise the anchor is already a real, persisted line item id.
        const createSubtotals = projectDraws
          .filter(draw => !draw.projectDrawSummary.id?.startsWith('temp-'))
          .flatMap(draw =>
            (draw.subtotals || [])
              .filter(subtotal => subtotal.id?.startsWith('temp-subtotal-') && !subtotal.isDeleted)
              .map(subtotal => ({
                drawId: draw.projectDrawSummary.id,
                label: subtotal.label,
                range: subtotal.range ?? null,
                ...(subtotal.anchorDrawLineItemId?.startsWith('temp-')
                  ? { anchorTempLineItemId: subtotal.anchorDrawLineItemId }
                  : { anchorDrawLineItemId: subtotal.anchorDrawLineItemId }),
              })),
          );

        // Already-persisted subtotals marked isDeleted — e.g. a payapp merge marks the project's
        // pre-existing subtotals for deletion since the payapp's own subtotals (createSubtotals,
        // above) fully replace them rather than trying to match old to new.
        const deleteSubtotalIds = projectDraws.flatMap(draw =>
          (draw.subtotals || [])
            .filter(subtotal => subtotal.isDeleted && !subtotal.id?.startsWith('temp-subtotal-'))
            .map(subtotal => subtotal.id),
        );

        // Generate audit messages
        const auditMessage = createAuditMessages(
          project,
          initialProject,
          createdDraws,
          updatedDraws,
          deletedDraws,
          lineItemsToCreate,
          lineItemsToUpdate,
          lineItemsToDelete,
          stateOptions,
        );

        // Consolidate all data into one DTO
        const projectDataDTO = {
          projectSummary: project?.projectSummary?.isEdited ? project.projectSummary : null,
          createdDraws: createDrawDTOs,
          updatedDraws: updateDrawDTOs,
          deletedDrawIds: deleteDrawIds,
          lineItems: {
            create: lineItemsToCreate,
            update: lineItemsToUpdate,
            delete: lineItemsToDelete,
            createSubtotals,
            deleteSubtotalIds,
          },
          auditMessage: auditMessage,
          notes: project?.notes ?? [],
          payappDrawId,
        };

        // Send a single API call with the consolidated DTO
        await globalUpdateProjectData(projectId, projectDataDTO);

        // Refetch the project data
        await refetchProject();
        toast.success('Project updated successfully.');
        setIsEditMode(false);
        setIsProjectDirty(false);
        setIsSaving(false);
        clearState();
      }
    } catch (error: any) {
      const errorMessage = error?.message || 'An error occurred while saving the project.';
      toast.error(errorMessage);
      setIsSaving(false);
    }
  };

  const refetchProject = async () => {
    try {
      setIsProjectLoading(true);

      // Refetch the project summary
      const updatedProjectSummary = await queryClient.fetchQuery([dataKeys.project, projectId], () => getProjectSummary(projectId));

      // Update the project state with the latest data
      setProject({
        projectSummary: deepCopy(updatedProjectSummary),
        projectDraws: [], // Optionally, this can also be updated if needed
      });

      // Refetch the project draws
      await queryClient.invalidateQueries([keys.projectDraws, projectId]);

      // Refetch the project line items and budgets for this project's draws (e.g. newly
      // created line items), since this query has its own key and isn't covered by the
      // projectDraws invalidation above.
      await queryClient.invalidateQueries([keys.projectLineItemsAndBudgets, projectId]);

      // Refetch the project history
      await queryClient.invalidateQueries([keys.projectHistory, projectId]);

      // Reset dirty flag
      setIsProjectDirty(false);
      clearState();
    } catch (error) {
      handleError(error);
    } finally {
      setIsProjectLoading(false);
    }
  };

  const handleCancel = async () => {
    if (isProjectDirty) {
      // Display confirmation dialog
      const isConfirmed = await getConfirmation({
        description: 'The project has unsaved changes. Are you sure you want to cancel?',
        confirmButton: {
          label: 'Continue',
          color: 'error',
        },
        cancelButton: {
          label: 'Cancel',
          color: 'secondary',
        },
      });

      // If the user cancels the confirmation, do nothing
      if (!isConfirmed) return;
    }

    // Call the refetchProject method to reset the project data
    await refetchProject();

    // Additional cleanup actions
    setIsEditMode(false);
  };
  return (
    <ProjectRenderer
      projectId={projectId}
      project={project}
      isLoadingProjectSummary={isLoadingProjectSummary}
      isErrorProjectSummary={isErrorProjectSummary}
      errorProjectSummary={errorProjectSummary}
      updateProject={updateProject}
      updateInitialProject={updateInitialProject}
      isProjectDirty={isProjectDirty}
      isProjectLoading={isProjectLoading}
      isSaving={isSaving}
      saveProject={saveProject}
      undoChanges={resetProject}
      handleCancel={handleCancel}
      refetchProject={refetchProject}
      isEditMode={isEditMode}
      setIsEditMode={setIsEditMode}
    />
  );
};
